import * as util from 'util';
import _ from 'lodash';
import {Component, EventEmitter, OnInit, Output} from "@angular/core";
import {ClipboardService} from 'ngx-clipboard';
import {DICTIONARY} from '../../../../dictionary';
import {RouteService} from '../../../../services/routeService';
import {NotificationService} from '../../../../services/notificationService';
import {GeneralService} from '../../../../services/generalService';

// CC: the MCP page of the account settings: where an AI assistant (Claude, ChatGPT, ...) connects to the account,
// whether this account may connect, how each client is set up and what the tools can do (catalog from the backend)
@Component({
	selector: 'account-mcp-component',
	templateUrl: './account-mcp.component.html',
})
export class AccountMcpComponent implements OnInit {
	// CC: the API key is a sibling tab of the Integrations page that hosts this component; the parent switches to it
	@Output() openApiTab = new EventEmitter();
	dic = DICTIONARY;
	showApiDocemntation = this.gs.showApiDocemntation;
	corpname = this.gs.corpname;
	mcpServer: any;
	toolCategories: {name: string, tools: any[], readCount: number, writeCount: number, expanded: boolean}[] = [];
	clients: any[] = [];
	selectedClient: any;
	error: string;

	examplePrompts = [
		'Show the emails quarantined in the last 24 hours and explain why each one was held',
		'What happened to the email from vendor@example.com to john@mycompany.com yesterday?',
		'Who clicked links from invoice-portal.example in the last week?',
		"Release the email with subject 'Q3 contract' to all of its recipients",
		"Add example.com to the senders allow list with the note 'approved vendor'",
		'Which admin changed the notification settings this month?',
		"Search the archive for emails from the CFO about 'wire transfer' in September",
		'List my managed plans and show the quarantine of admin@customer.com for the last 3 days'
	];

	constructor(private gs: GeneralService,
				private rs: RouteService,
				private ns: NotificationService,
				private clipboard: ClipboardService) {
	}

	ngOnInit() {
		this.rs.getMcpServerInfo().then((mcpServer) => {
			this.mcpServer = mcpServer;
			// the tool descriptions are written for the model (when to call, what to call first); the page shows their first sentence
			mcpServer.tools.forEach(tool => tool.summary = tool.description.split(/(?<=\.)\s+/)[0]);
			this.toolCategories = _.map(_.groupBy(mcpServer.tools, 'category'), (tools, name) => ({
				name,
				tools,
				readCount: tools.filter(tool => tool.read_only).length,
				writeCount: tools.filter(tool => !tool.read_only).length,
				expanded: false
			}));
			this.clients = this.buildClients(mcpServer.url);
			this.selectedClient = this.clients[0];
		}).catch((err) => {
			this.error = err?.data?.message || 'Failed to load the MCP server information';
		});
	}

	copyToClipboard(text: string, label: string) {
		this.clipboard.copy(text);
		this.ns.showInfoMessage(util.format(this.dic.MESSAGES.copyClipboard, label));
	}

	// the setup of every supported MCP client. The command (when the client has one) contains the server URL
	private buildClients(url: string) {
		return [
			{
				key: 'claude', name: 'Claude (claude.ai, Desktop, mobile)', icon: 'far fa-comments',
				steps: [
					'In claude.ai go to Customize > Connectors and click "Add custom connector" (on a Team or Enterprise plan an Owner adds it once under Organization settings > Connectors).',
					'Enter the name "' + this.corpname + '" and the server URL below. Leave the OAuth client ID and secret empty (choose "Sign in now" or "Use Claude\'s published identity" if asked).',
					'Click Add, then Connect. Log in with your ' + this.corpname + ' account and click Allow on the consent page.',
					'In a chat click +, select Connectors and turn ' + this.corpname + ' on. Claude asks for your approval before each tool call.'
				],
				command: url, commandLabel: 'Server URL'
			},
			{
				key: 'claudeCode', name: 'Claude Code (CLI)', icon: 'far fa-terminal',
				steps: [
					'Add the server with the command below ("--scope user" makes it available in all your projects).',
					'Start Claude Code, run /mcp, select "trustifi" and choose Authenticate (or run "claude mcp login trustifi").',
					'Log in in the browser window that opens and click Allow. The consent page warns that the redirect goes to localhost: that is expected for a CLI.',
					'Check the connection with "claude mcp list". Sign out with "claude mcp logout trustifi".'
				],
				command: 'claude mcp add --transport http --scope user trustifi ' + url, commandLabel: 'Command'
			},
			{
				key: 'chatgpt', name: 'ChatGPT (web)', icon: 'far fa-browser',
				steps: [
					'Go to chatgpt.com/plugins, click +, then "Add custom MCP server" (your workspace settings decide whether you may add one).',
					'Enter the name "' + this.corpname + '" and the server URL below. For authentication choose OAuth and leave the client credentials empty.',
					'Accept the risk warning and create the plugin, then install it, log in with your ' + this.corpname + ' account and click Allow.',
					'In a chat type @, select ' + this.corpname + ' and ask. Read-only tools run without a prompt; ChatGPT asks you to confirm every action.'
				],
				command: url, commandLabel: 'Server URL'
			},
			{
				key: 'codex', name: 'Codex CLI / ChatGPT desktop', icon: 'far fa-display',
				steps: [
					'Add the server with the command below. The Codex CLI, the ChatGPT desktop app and the Codex IDE extension share this configuration.',
					'Sign in with "codex mcp login trustifi", log in in the browser window that opens and click Allow.',
					'Check it with "codex mcp list" or /mcp inside the codex terminal. Sign out with "codex mcp logout trustifi".'
				],
				command: 'codex mcp add trustifi --url ' + url, commandLabel: 'Command'
			}
		];
	}
}
